import { useEffect, useRef } from 'react';

const PUBLISHER_ID = import.meta.env.VITE_ADSENSE_PUBLISHER_ID || '';
let scriptPromise = null;

/**
 * Lazy-load the AdSense script. Resolves when the script is ready.
 * Called by each MenuAd instance, but only loaded once globally.
 */
function loadAdSense() {
  if (!PUBLISHER_ID) return Promise.reject(new Error('AdSense publisher ID not configured'));
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.adsbygoogle) return resolve();
    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${PUBLISHER_ID}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load AdSense'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * A single AdSense ad unit. Renders nothing if AdSense isn't configured.
 *
 * Props:
 *   slot     — AdSense ad unit ID (the "data-ad-slot" value)
 *   format   — 'auto' | 'fluid' | 'horizontal' | etc. Defaults to 'auto'.
 *   layout   — optional 'in-article' for native-style ads in feeds
 *   minHeight — reserve vertical space so the layout doesn't jump
 */
export default function MenuAd({ slot, format = 'auto', layout, minHeight = 90 }) {
  const insRef = useRef(null);
  const pushedRef = useRef(false);

  useEffect(() => {
    if (!PUBLISHER_ID || !slot) return;
    if (pushedRef.current) return;

    let cancelled = false;
    loadAdSense()
      .then(() => {
        if (cancelled || pushedRef.current) return;
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          pushedRef.current = true;
        } catch (err) {
          console.warn('[AdSense] push failed:', err.message);
        }
      })
      .catch(err => console.warn('[AdSense]', err.message));
    return () => { cancelled = true; };
  }, [slot]);

  // Bail silently if not configured — useful in dev when env vars are missing.
  if (!PUBLISHER_ID || !slot) return null;

  return (
    <div style={{ minHeight: `${minHeight}px`, margin: '12px 0' }}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', textAlign: 'center' }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
        {...(layout ? { 'data-ad-layout': layout } : {})}
      />
    </div>
  );
}
